import { useEffect } from 'react';

/** One site-wide motion system: reveals (IntersectionObserver) + scroll parallax (single rAF). */
export default function useReveal() {
  useEffect(() => {
    const SEL = '.reveal, .mask, .line-grow';
    let io: IntersectionObserver | null = null;
    const show = (el: Element) => el.classList.add('in');
    const track = (root: Document | Element) => {
      const found: Element[] = [];
      if (root instanceof Element && root.matches(SEL)) found.push(root);
      root.querySelectorAll(SEL).forEach((el) => found.push(el));
      found.forEach((el) => {
        if (el.classList.contains('in')) return;
        if (io) io.observe(el); else show(el);
      });
    };
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              show(e.target);
              io?.unobserve(e.target);
            }
          });
        },
        { threshold: 0, rootMargin: '0px 0px -6% 0px' },
      );
    }
    track(document);
    // pick up elements re-mounted later (re-renders / hot reload)
    const mo = new MutationObserver((muts) => {
      muts.forEach((m) => m.addedNodes.forEach((n) => { if (n instanceof Element) track(n); }));
    });
    mo.observe(document.body, { childList: true, subtree: true });
    // safety net: anything already above the fold-line after load is revealed
    const safety = window.setTimeout(() => {
      document.querySelectorAll(SEL).forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight) show(el);
      });
    }, 1500);
    const cleanupReveal = () => { io?.disconnect(); mo.disconnect(); window.clearTimeout(safety); };

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const small = window.matchMedia('(max-width: 767px)').matches;
    const items = Array.from(document.querySelectorAll<HTMLElement>('[data-parallax]'));
    if (reduce || small || !items.length) return cleanupReveal;

    const visible = new Set<HTMLElement>();
    const pio = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const el = e.target as HTMLElement;
        if (e.isIntersecting) visible.add(el); else visible.delete(el);
      });
      schedule();
    }, { rootMargin: '10% 0px' });

    let ticking = false;
    const update = () => {
      ticking = false;
      const vh = window.innerHeight;
      visible.forEach((el) => {
        const host = el.parentElement;
        if (!host) return;
        const r = host.getBoundingClientRect();
        const p = (r.top + r.height / 2 - vh / 2) / (vh + r.height); // -0.5..0.5
        const speed = Number(el.dataset.parallax) || 0.08;
        const y = Math.max(-1, Math.min(1, p * 2)) * speed * r.height * 0.5;
        el.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0) scale(${el.dataset.scale || '1.14'})`;
      });
    };
    function schedule() {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }
    items.forEach((el) => pio.observe(el));
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cleanupReveal();
      pio.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, []);
}